import { useLocalParticipant } from "@livekit/components-react";
import { useEffect, useRef } from "react";
import {
  useConferenceSettingsModal,
  useConferenceSettingsStore,
} from "@features/appointment/conference-settings";
import { useAppointmentNotification } from "../appointment-wrapper/use-appointment-notification";

const STATS_POLL_INTERVAL_MS = 2000;
// Порог потерь, при котором предлагаем понизить качество (в процентах за интервал опроса)
const PACKET_LOSS_THRESHOLD_PERCENT = 5;
// Гистерезис: ниже этого значения считаем, что сеть восстановилась, и разрешаем показ снова
const PACKET_LOSS_RECOVER_PERCENT = 2;
// Сколько подряд опросов с высокими потерями ждём перед показом (~6 секунд)
const SUSTAINED_BAD_POLLS = 3;
const NOTIFICATION_ID = "packet-loss-audio-quality";
const NOTIFICATION_DURATION_MS = 10000;
// Минимальное качество из настроек — предлагать понизить дальше некуда
const LOWEST_BITRATE = "traffic-saving";

const getAudioStats = (report) => {
  let outbound = null;
  let remoteInbound = null;

  for (const stat of report.values()) {
    if (stat.type === "outbound-rtp" && stat.kind === "audio" && !stat.isRemote) {
      outbound = stat;
    }

    if (stat.type === "remote-inbound-rtp" && stat.kind === "audio") {
      remoteInbound = stat;
    }
  }

  return { outbound, remoteInbound };
};

/**
 * Следит за потерями пакетов на опубликованном микрофоне.
 * Если потери держатся выше порога несколько опросов подряд — показывает уведомление
 * с предложением понизить качество аудио и кнопкой открытия настроек конференции.
 */
export const usePacketLossSuggestion = () => {
  const { microphoneTrack, isMicrophoneEnabled } = useLocalParticipant();
  const { selectedAudioBitrate } = useConferenceSettingsStore();
  const { open } = useConferenceSettingsModal();
  const { addNotification, removeNotification } = useAppointmentNotification();

  const previousStatsRef = useRef(null);
  const badPollsRef = useRef(0);
  const hasSuggestedRef = useRef(false);

  useEffect(() => {
    const localTrack = microphoneTrack?.track;

    if (!isMicrophoneEnabled || !localTrack?.getRTCStatsReport) {
      previousStatsRef.current = null;
      badPollsRef.current = 0;
      return undefined;
    }

    let isCancelled = false;
    previousStatsRef.current = null;

    const checkPacketLoss = async () => {
      try {
        const report = await localTrack.getRTCStatsReport();

        if (!report || isCancelled) {
          return;
        }

        const { outbound, remoteInbound } = getAudioStats(report);
        const previous = previousStatsRef.current;

        previousStatsRef.current = {
          packetsSent: outbound?.packetsSent,
          packetsLost: remoteInbound?.packetsLost,
        };

        if (!previous || !outbound || !remoteInbound) {
          return;
        }

        if (
          !Number.isFinite(previous.packetsSent) ||
          !Number.isFinite(previous.packetsLost) ||
          !Number.isFinite(outbound.packetsSent) ||
          !Number.isFinite(remoteInbound.packetsLost)
        ) {
          return;
        }

        // Считаем потери по дельте между опросами, а не по суммарным счётчикам —
        // так реагируем на текущее состояние сети, а не на историю соединения
        const sentDelta = outbound.packetsSent - previous.packetsSent;
        const lostDelta = remoteInbound.packetsLost - previous.packetsLost;
        const totalDelta = sentDelta + lostDelta;

        if (totalDelta <= 0) {
          return;
        }

        const lossPercent = (lostDelta / totalDelta) * 100;

        if (lossPercent >= PACKET_LOSS_THRESHOLD_PERCENT) {
          badPollsRef.current += 1;
        } else {
          badPollsRef.current = 0;

          if (lossPercent < PACKET_LOSS_RECOVER_PERCENT) {
            hasSuggestedRef.current = false;
          }
        }

        if (badPollsRef.current < SUSTAINED_BAD_POLLS || hasSuggestedRef.current) {
          return;
        }

        badPollsRef.current = 0;
        hasSuggestedRef.current = true;

        // Уже выбран минимальный битрейт — предлагать понизить дальше некуда
        if (selectedAudioBitrate === LOWEST_BITRATE) {
          return;
        }

        addNotification({
          id: NOTIFICATION_ID,
          type: "network",
          duration: NOTIFICATION_DURATION_MS,
          message:
            "Ваше интернет-соединение нестабильно",
          action: {
            label: "Открыть настройки",
            onClick: () => {
              removeNotification(NOTIFICATION_ID);
              open();
            },
          },
        });
      } catch {
        // Статистика недоступна — просто пропускаем опрос
      }
    };

    void checkPacketLoss();
    const intervalId = window.setInterval(() => {
      void checkPacketLoss();
    }, STATS_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    addNotification,
    isMicrophoneEnabled,
    microphoneTrack,
    open,
    removeNotification,
    selectedAudioBitrate,
  ]);
};
